import { ApiError } from 'api/schema/core/ApiError'
import type { ApiRequestOptions } from 'api/schema/core/ApiRequestOptions'
import { OpenAPI } from 'api/schema/core/OpenAPI'
import { request as __request } from 'api/schema/core/request'
import type { UserAudience } from 'hooks/user'

export type OrganizationMember = {
  id: number
  email: string
  first_name: string
  last_name: string
  audience: UserAudience
  is_active: boolean
  avatar_url: string | null
  can_open: boolean
}

export type OrganizationMemberCandidate = {
  id: number
  email: string
  first_name: string
  last_name: string
}

export type OrganizationAccessUser = {
  user_id: number
  email: string
  first_name: string
  last_name: string
}

export type IamCannotEditReason = 'self' | 'ask_higher_manager' | 'partial_scope' | 'empty_acl'

export type OrganizationRef = {
  id: number
  name: string
  slug: string
  is_active: boolean
  member_count: number
  can_assign_internal_serviceman: boolean
}

export type IamUser = {
  id: number
  email: string
  first_name: string
  last_name: string
  audience: UserAudience
  organization: OrganizationRef | null
  is_active: boolean
  created_by_id: number | null
  facility_ids: number[]
  robot_ids: number[]
  visible_robot_ids: number[] | null
  robot_station_ids: number[]
  can_edit_identity: boolean
  can_assign_objects: boolean
  cannot_edit_reason: IamCannotEditReason | null
  avatar_url: string | null
}

export type IamUserWrite = {
  email?: string
  first_name?: string
  last_name?: string
  audience?: UserAudience
  organization_id?: number | null
  facility_ids?: number[]
  robot_ids?: number[]
}

export type IamUserCreate = {
  email: string
  first_name: string
  last_name: string
  audience: UserAudience
  password: string
  organization_id?: number | null
  facility_ids?: number[]
  robot_ids?: number[]
}

export type IamUserListResponse = {
  results: IamUser[]
  total: number
  page_number: number
  page_size: number
}

const throwApiError = (url: string, method: ApiRequestOptions['method'], response: Response, body: unknown): never => {
  throw new ApiError(
    { method, url },
    {
      url,
      ok: false,
      status: response.status,
      statusText: response.statusText,
      body,
    },
    response.statusText || 'Request failed'
  )
}

export function iamErrorReason(error: unknown): string | null {
  if (!(error instanceof ApiError) || !error.body || typeof error.body !== 'object') return null
  const details = (error.body as Record<string, unknown>).details
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    const reason = (details as Record<string, unknown>).reason
    if (typeof reason === 'string') return reason
  }
  return null
}

export function organizationDeactivateRequired(error: unknown): number | null {
  if (!(error instanceof ApiError) || !error.body || typeof error.body !== 'object') return null
  const record = error.body as Record<string, unknown>
  const details = record.details
  const source = details && typeof details === 'object' && !Array.isArray(details)
    ? details as Record<string, unknown>
    : record
  if (source.requires_user_deactivation !== true) return null
  const count = Number(source.user_count)
  return Number.isFinite(count) ? count : 0
}

export function iamErrorMessage(error: unknown, field?: string): string | null {
  if (!(error instanceof ApiError) || !error.body || typeof error.body !== 'object') return null
  const record = error.body as Record<string, unknown>
  const details = record.details
  const sources: unknown[] = [record, details]
  for (const source of sources) {
    if (!source || typeof source !== 'object' || Array.isArray(source)) continue
    const obj = source as Record<string, unknown>
    if (field) {
      const value = obj[field]
      if (Array.isArray(value) && value.length && typeof value[0] === 'string') return value[0]
      if (typeof value === 'string') return value
    }
    if (typeof obj.detail === 'string') return obj.detail
  }
  return null
}

const normalizeOrganization = (raw: unknown): OrganizationRef | null => {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const id = Number(record.id)
  if (!Number.isFinite(id)) return null
  return {
    id,
    name: String(record.name || ''),
    slug: String(record.slug || ''),
    is_active: record.is_active !== false,
    member_count: Number(record.member_count) || 0,
    can_assign_internal_serviceman: Boolean(record.can_assign_internal_serviceman),
  }
}

const normalizeUser = (raw: any): IamUser => ({
  id: Number(raw.id),
  email: String(raw.email || ''),
  first_name: String(raw.first_name || ''),
  last_name: String(raw.last_name || ''),
  audience: raw.audience,
  organization: normalizeOrganization(raw.organization),
  is_active: Boolean(raw.is_active),
  created_by_id: raw.created_by_id == null ? null : Number(raw.created_by_id),
  facility_ids: Array.isArray(raw.facility_ids) ? raw.facility_ids.map(Number) : [],
  robot_ids: Array.isArray(raw.robot_ids) ? raw.robot_ids.map(Number) : [],
  visible_robot_ids: raw.visible_robot_ids == null
    ? null
    : Array.isArray(raw.visible_robot_ids)
      ? raw.visible_robot_ids.map(Number)
      : [],
  robot_station_ids: Array.isArray(raw.robot_station_ids) ? raw.robot_station_ids.map(Number) : [],
  can_edit_identity: Boolean(raw.can_edit_identity),
  can_assign_objects: Boolean(raw.can_assign_objects),
  cannot_edit_reason: raw.cannot_edit_reason || null,
  avatar_url: typeof raw.avatar_url === 'string' && raw.avatar_url ? raw.avatar_url : null,
})

export const IamApi = {
  listUsers: async (params: {
    page?: number
    pageSize?: number
    search?: string
    isActive?: boolean | null
    audience?: UserAudience
    organization?: string | number
    facilityId?: number
  }): Promise<IamUserListResponse> => {
    const query: Record<string, unknown> = {}
    if (params.page) query.page = params.page
    if (params.pageSize) query.page_size = params.pageSize
    if (params.search) query.search = params.search
    if (params.isActive === true) query.is_active = 'true'
    if (params.isActive === false) query.is_active = 'false'
    if (params.audience) query.audience = params.audience
    if (params.organization != null && params.organization !== '') {
      query.organization = String(params.organization)
    }
    if (params.facilityId) query.facility = params.facilityId
    const response = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/acc/users/',
      query,
    })) as any
    const results = Array.isArray(response?.results) ? response.results.map(normalizeUser) : []
    return {
      results,
      total: Number(response?.total ?? results.length),
      page_number: Number(response?.page_number ?? 1),
      page_size: Number(response?.page_size ?? results.length),
    }
  },

  listOrganizations: async (): Promise<OrganizationRef[]> => {
    const response = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/acc/organizations/',
    })) as unknown
    if (!Array.isArray(response)) return []
    return response.map(normalizeOrganization).filter((item): item is OrganizationRef => item != null)
  },

  createOrganization: async (payload: { name: string }): Promise<OrganizationRef> => {
    const response = await __request(OpenAPI, {
      method: 'POST',
      url: '/api/acc/organizations/',
      body: payload,
      mediaType: 'application/json',
    })
    const org = normalizeOrganization(response)
    if (!org) throw new Error('Invalid organization response')
    return org
  },

  updateOrganization: async (id: number, payload: { name?: string }): Promise<OrganizationRef> => {
    const response = await __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/acc/organizations/{id}/',
      path: { id },
      body: payload,
      mediaType: 'application/json',
    })
    const org = normalizeOrganization(response)
    if (!org) throw new Error('Invalid organization response')
    return org
  },

  listOrganizationMembers: async (id: number): Promise<{
    members: OrganizationMember[]
    candidates: OrganizationMemberCandidate[]
  }> => {
    const response = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/acc/organizations/{id}/members/',
      path: { id },
    })) as { members?: unknown; candidates?: unknown }
    const mapMember = (item: unknown): OrganizationMember | null => {
      if (!item || typeof item !== 'object') return null
      const record = item as Record<string, unknown>
      const memberId = Number(record.id)
      if (!Number.isFinite(memberId)) return null
      return {
        id: memberId,
        email: String(record.email || ''),
        first_name: String(record.first_name || ''),
        last_name: String(record.last_name || ''),
        audience: record.audience as UserAudience,
        is_active: record.is_active !== false,
        avatar_url: typeof record.avatar_url === 'string' && record.avatar_url ? record.avatar_url : null,
        can_open: Boolean(record.can_open),
      }
    }
    const mapCandidate = (item: unknown): OrganizationMemberCandidate | null => {
      if (!item || typeof item !== 'object') return null
      const record = item as Record<string, unknown>
      const memberId = Number(record.id)
      if (!Number.isFinite(memberId)) return null
      return {
        id: memberId,
        email: String(record.email || ''),
        first_name: String(record.first_name || ''),
        last_name: String(record.last_name || ''),
      }
    }
    return {
      members: Array.isArray(response?.members)
        ? response.members.map(mapMember).filter((item): item is OrganizationMember => item != null)
        : [],
      candidates: Array.isArray(response?.candidates)
        ? response.candidates.map(mapCandidate).filter((item): item is OrganizationMemberCandidate => item != null)
        : [],
    }
  },

  addOrganizationMember: async (id: number, userId: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'POST',
      url: '/api/acc/organizations/{id}/members/',
      path: { id },
      body: { user_id: userId },
      mediaType: 'application/json',
    })
  },

  removeOrganizationMember: async (id: number, userId: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/acc/organizations/{id}/members/{user_id}/',
      path: { id, user_id: userId },
    })
  },

  listOrganizationAccess: async (id: number): Promise<{
    grants: OrganizationAccessUser[]
    candidates: OrganizationAccessUser[]
  }> => {
    const response = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/acc/organizations/{id}/access/',
      path: { id },
    })) as { grants?: unknown; candidates?: unknown }
    const mapUser = (item: unknown): OrganizationAccessUser | null => {
      if (!item || typeof item !== 'object') return null
      const record = item as Record<string, unknown>
      const userId = Number(record.user_id)
      if (!Number.isFinite(userId)) return null
      return {
        user_id: userId,
        email: String(record.email || ''),
        first_name: String(record.first_name || ''),
        last_name: String(record.last_name || ''),
      }
    }
    return {
      grants: Array.isArray(response?.grants)
        ? response.grants.map(mapUser).filter((item): item is OrganizationAccessUser => item != null)
        : [],
      candidates: Array.isArray(response?.candidates)
        ? response.candidates.map(mapUser).filter((item): item is OrganizationAccessUser => item != null)
        : [],
    }
  },

  grantOrganizationAccess: async (id: number, userId: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'POST',
      url: '/api/acc/organizations/{id}/access/',
      path: { id },
      body: { user_id: userId },
      mediaType: 'application/json',
    })
  },

  revokeOrganizationAccess: async (id: number, userId: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/acc/organizations/{id}/access/{user_id}/',
      path: { id, user_id: userId },
    })
  },

  deleteOrganization: async (id: number, deactivateUsers = false): Promise<void> => {
    await __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/acc/organizations/{id}/',
      path: { id },
      query: deactivateUsers ? { deactivate_users: 'true' } : undefined,
    })
  },

  listFilterOptions: async (): Promise<{ organizations: OrganizationRef[] }> => {
    const response = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/acc/users/filter-options/',
    })) as any
    const organizations = Array.isArray(response?.organizations)
      ? response.organizations
          .map(normalizeOrganization)
          .filter((item: OrganizationRef | null): item is OrganizationRef => item != null)
      : []
    return { organizations }
  },

  getUser: async (id: number): Promise<IamUser> => {
    const response = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/acc/users/{id}/',
      path: { id },
    })
    return normalizeUser(response)
  },

  createUser: async (payload: IamUserCreate): Promise<{ user: IamUser; created: boolean }> => {
    const url = `${OpenAPI.BASE}/api/acc/users/`
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const body = await response.json().catch(() => undefined)
    if (!response.ok) {
      throwApiError(url, 'POST', response, body)
    }
    return { user: normalizeUser(body), created: response.status === 201 }
  },

  updateUser: async (id: number, payload: IamUserWrite): Promise<IamUser> => {
    const response = await __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/acc/users/{id}/',
      path: { id },
      body: payload,
      mediaType: 'application/json',
    })
    return normalizeUser(response)
  },

  deactivateUser: async (id: number): Promise<IamUser> => {
    const response = await __request(OpenAPI, {
      method: 'POST',
      url: '/api/acc/users/{id}/deactivate/',
      path: { id },
    })
    return normalizeUser(response)
  },

  reactivateUser: async (id: number): Promise<IamUser> => {
    const response = await __request(OpenAPI, {
      method: 'POST',
      url: '/api/acc/users/{id}/reactivate/',
      path: { id },
    })
    return normalizeUser(response)
  },

  setPassword: async (id: number, password: string): Promise<IamUser> => {
    const response = await __request(OpenAPI, {
      method: 'POST',
      url: '/api/acc/users/{id}/set-password/',
      path: { id },
      body: { password },
      mediaType: 'application/json',
    })
    return normalizeUser(response)
  },
}
